import React, { useEffect } from "react";
import { Link, useParams, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowLeft, MessageCircle, Check } from "lucide-react";
import { getMotorBySlug, MOTORCYCLES } from "../data/motorcycles";
import { buildWaLink, formatIDR } from "../lib/whatsapp";
import MotorCard from "../components/MotorCard";

export default function MotorDetail() {
  const { slug } = useParams();
  const navigate = useNavigate();
  const motor = getMotorBySlug(slug);

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: "smooth" });
  }, [slug]);

  if (!motor) {
    return (
      <div className="pt-16 pb-12 text-center">
        <p className="text-slate-600">Motor tidak ditemukan.</p>
        <button
          onClick={() => navigate("/")}
          className="mt-4 inline-flex items-center gap-2 px-4 py-2 rounded-full bg-[#002d62] text-white text-sm"
        >
          <ArrowLeft className="w-4 h-4" /> Kembali
        </button>
      </div>
    );
  }

  const related = MOTORCYCLES.filter(
    (m) => m.category === motor.category && m.slug !== motor.slug
  ).slice(0, 4);

  return (
    <div className="py-6 md:py-10 bg-white" data-testid={`page-motor-${motor.slug}`}>
      <div className="y-container">
        <Link
          to="/"
          data-testid="detail-back"
          className="inline-flex items-center gap-1 text-xs sm:text-sm text-slate-600 hover:text-[#002d62] transition-colors"
        >
          <ArrowLeft className="w-4 h-4" /> Kembali ke Katalog
        </Link>

        <div className="mt-4 grid gap-6 md:gap-10 md:grid-cols-2 items-start">
          <motion.div
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.4 }}
            className="rounded-2xl bg-slate-50 border border-slate-200 aspect-[4/3] flex items-center justify-center overflow-hidden"
          >
            <img
              src={motor.image}
              alt={motor.name}
              className="w-full h-full object-contain p-6"
              data-testid="detail-main-image"
            />
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, delay: 0.1 }}
          >
            <span className="inline-block px-2.5 py-0.5 rounded-full text-[10px] font-bold uppercase tracking-wider bg-slate-100 text-slate-700">
              {motor.category} · {motor.engine_cc}cc
            </span>
            <h1 className="mt-2 font-display font-black text-2xl sm:text-4xl text-slate-900 leading-tight">
              {motor.name}
            </h1>

            <div className="mt-4 rounded-xl bg-gradient-to-br from-[#002d62] to-[#0a1f44] text-white p-4">
              <p className="text-[10px] uppercase tracking-wider text-white/70">{motor.priceLabel}</p>
              <p className="font-display font-black text-2xl sm:text-3xl" data-testid="detail-price">
                {formatIDR(motor.price_idr)}
              </p>
              <p className="mt-1 text-[10px] text-white/60">*Harga Jakarta referensi. Konfirmasi OTR Kalbar via WA.</p>
            </div>

            <div className="mt-4 flex flex-wrap gap-2">
              <a
                href={buildWaLink(motor.name)}
                target="_blank"
                rel="noopener noreferrer"
                data-testid="detail-wa-cta"
                className="flex-1 inline-flex items-center justify-center gap-2 px-4 py-2.5 rounded-full bg-[#25D366] hover:bg-[#1ebe57] text-white text-sm font-semibold transition"
              >
                <MessageCircle className="w-4 h-4" /> Pesan via WhatsApp
              </a>
            </div>

            <ul className="mt-5 space-y-1.5 text-xs sm:text-sm text-slate-700">
              {[
                "Unit 100% baru, garansi resmi Yamaha",
                "Bantu pengajuan kredit banyak leasing",
                "Antar sampai rumah area Pontianak & Kalimantan Barat",
                "Bantu urus STNK & BPKB",
              ].map((x) => (
                <li key={x} className="flex items-start gap-2">
                  <Check className="w-4 h-4 text-emerald-600 mt-0.5 shrink-0" /> {x}
                </li>
              ))}
            </ul>
          </motion.div>
        </div>

        {related.length > 0 && (
          <section className="mt-12">
            <h2 className="font-display font-bold text-lg sm:text-xl text-slate-900 mb-4">
              Motor {motor.category} Lainnya
            </h2>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3 sm:gap-4">
              {related.map((m, i) => (
                <MotorCard key={m.slug} motor={m} index={i} />
              ))}
            </div>
          </section>
        )}
      </div>
    </div>
  );
}
